import { useUser } from '@trustpad/launchpad';
import { useClaimAction } from '@trustpad/launchpad/dist/staking/hooks/useClaimAction';
import { StakingContractV2 } from '@trustpad/launchpad/dist/staking/hooks/useStakingContract';

import BN from 'bn.js';
import dayjs from 'dayjs';
import * as React from 'react';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import CountUp from 'react-countup';
import { FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { DateTimeFormat } from '~config/constants';
import { TPAD } from '~config/tokens';
import { ActionButton, Dialog } from '~features/ui';
import { fNum, toEthN, toWeiBN } from '~utils';

const CLAIM_FEE = 30;

export function ClaimBlock({
  pendingRewards,
  stakingContract,
  refresh,
  maturityAt,
  halted,
}: {
  pendingRewards: BN;
  stakingContract: StakingContractV2;
  refresh: VoidFunction;
  maturityAt: number;
  halted?: boolean;
}) {
  const { account } = useUser();
  const { claim, isLoading } = useClaimAction({
    contract: stakingContract,
    refresh,
    decimals: TPAD.decimals,
  });
  const [restakeLoading, setRestakeLoading] = useState(false);

  if (pendingRewards.lt(toWeiBN(0.0001))) {
    return null;
  }

  const restake = () => {
    setRestakeLoading(true);
    stakingContract.methods
      .stakePendingRewards()
      .send({ from: account })
      .then(() => {
        toast.success(
          `Successfully staked ${fNum(
            pendingRewards,
          )} TPAD. Your total staked amount has increased.`,
        );
        refresh();
      })
      .catch((e) => toast.error(`Failed to re-stake`))
      .finally(() => setRestakeLoading(false));
  };

  const rewardsMatured = Date.now() / 1000 > maturityAt;
  const isClaimDisabled =
    isLoading || restakeLoading || pendingRewards.ltn(1) || halted;
  const isRestakeDisabled =
    isLoading || restakeLoading || pendingRewards.ltn(1) || halted;
  const feeAmount = pendingRewards.muln(CLAIM_FEE).divn(100);
  const endAmount = pendingRewards.sub(feeAmount);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-6 justify-between flex-wrap">
        <span className="text-3xl brand-text">
          <CountUp end={toEthN(pendingRewards)} decimals={2} preserveValue />{' '}
          TPAD
        </span>

        <div className="ml-auto space-x-2">
          {rewardsMatured ? (
            <>
              <Dialog
                title="Claim reward"
                openButton={(open) => (
                  <ActionButton
                    isLoading={isLoading}
                    isDisabled={isClaimDisabled}
                    onClick={open}
                    className="w-28">
                    Claim
                  </ActionButton>
                )}
                buttons={({ close }) => (
                  <>
                    <ActionButton
                      isLoading={isLoading}
                      isDisabled={isClaimDisabled}
                      onClick={() => claim().then(close)}
                      className="w-full">
                      Claim (30% fee)
                    </ActionButton>
                    <ActionButton
                      isLoading={restakeLoading}
                      isDisabled={isClaimDisabled}
                      onClick={restake}>
                      Re-stake (0% fee)
                    </ActionButton>
                    <Button
                      variant="outline-secondary"
                      className="w-full col-span-2"
                      onClick={close}>
                      Cancel
                    </Button>
                  </>
                )}>
                <Dialog.SubTitle>
                  You will receive <b>{fNum(endAmount)} TPAD</b>
                </Dialog.SubTitle>
                <Dialog.Sections>
                  <Dialog.SectionItem iconStatus="warning">
                    <p>
                      <b>There is currently 30% tax/fee on claims.</b> This fee
                      will be re-distributed to long-term stakers, and a part of
                      it to be burned. Stay tuned for more details.
                    </p>
                  </Dialog.SectionItem>
                  <Dialog.SectionItem iconStatus="info">
                    To avoid the fee you can{' '}
                    <b>re-stake your rewards with 0% fee</b>.
                  </Dialog.SectionItem>
                </Dialog.Sections>
              </Dialog>
              <ActionButton
                isLoading={restakeLoading}
                isDisabled={isRestakeDisabled}
                onClick={restake}
                className="w-28">
                Re-stake
              </ActionButton>
            </>
          ) : (
            <div className="space-x-2">
              <ActionButton
                isDisabled
                type="secondaryOutline"
                onClick={() => {}}
                className="w-28">
                Claim
              </ActionButton>
              <ActionButton
                isLoading={restakeLoading}
                isDisabled={isClaimDisabled}
                onClick={restake}
                className="w-28">
                Re-stake
              </ActionButton>
            </div>
          )}
        </div>
      </div>

      {rewardsMatured ? (
        <div className="text-sm text-muted">
          <FaCheck className="text-green-500 inline-block mr-1" />
          <span className="align-middle">
            Matured. Once claimed, it will require some time to mature until you
            can claim again.
          </span>
        </div>
      ) : (
        <div className="text-sm text-muted">
          ⚠️ Not yet matured. Claimable on maturity:{' '}
          <b>{dayjs.unix(maturityAt).utc().format(DateTimeFormat.noYear)}</b>{' '}
          <br />
          Or you can <b>stake the rewards</b>. Doing that won't extend the lock
          period.
        </div>
      )}
    </div>
  );
}
